import { useEffect, useState, type FormEvent } from "react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useLocation } from "react-router-dom";

import {
  submitFeedbackReport,
  type FeedbackEnvironment,
  type FeedbackKind,
  type FeedbackSeverity,
} from "../../api/feedback";
import {
  createFeedbackBundle,
  installFeedbackInstrumentation,
  recordFeedbackRoute,
} from "../../lib/feedbackContext";
import "./FeedbackReporter.css";

const DEV_HOSTS = new Set(["thread.dev-simulation.cloud", "thread-dev.flexcompute.com"]);
const PROD_HOSTS = new Set(["thread.simulation.cloud", "thread.flexcompute.com"]);
const OPEN_FEEDBACK_EVENT = "thread-feedback:open";

type SubmitState =
  | { type: "idle"; message: "" }
  | { type: "success"; message: string; url?: string | null }
  | { type: "error"; message: string };

interface FeedbackReporterProps {
  env?: string;
  hostname?: string;
}

export function resolveFeedbackEnvironment(
  env = import.meta.env.VITE_THREAD_ENV,
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
): FeedbackEnvironment {
  if (PROD_HOSTS.has(hostname)) return "prod";
  if (DEV_HOSTS.has(hostname)) return "dev";
  if (env === "prod") return "prod";
  if (env === "dev") return "dev";
  return "local";
}

export function openThreadFeedbackReporter() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_FEEDBACK_EVENT));
}

export function FeedbackDevBadge({ env, hostname }: FeedbackReporterProps = {}) {
  const environment = resolveFeedbackEnvironment(env, hostname);
  if (environment !== "dev") return null;

  return (
    <button
      className="thread-feedback-dev-badge"
      type="button"
      onClick={openThreadFeedbackReporter}
      aria-label="Report from Thread dev"
      title="Report from Thread dev"
      data-feedback-exclude="true"
    >
      <span className="thread-feedback-dev-badge__env">DEV</span>
      <span className="thread-feedback-dev-badge__label">Report</span>
    </button>
  );
}

export function FeedbackReporter({ env, hostname }: FeedbackReporterProps = {}) {
  const location = useLocation();
  const environment = resolveFeedbackEnvironment(env, hostname);
  const isDev = environment === "dev";
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>("issue");
  const [severity, setSeverity] = useState<FeedbackSeverity>("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>({ type: "idle", message: "" });

  useEffect(() => installFeedbackInstrumentation(), []);

  useEffect(() => {
    const openReporter = () => setOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, openReporter);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, openReporter);
  }, []);

  useEffect(() => {
    recordFeedbackRoute(`${location.pathname}${location.search}`, document.title);
  }, [location.pathname, location.search]);

  const canSubmit = title.trim().length >= 3 && description.trim().length >= 3 && !submitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setSubmitState({ type: "idle", message: "" });
    try {
      const bundle = await createFeedbackBundle({ includeScreenshot });
      const response = await submitFeedbackReport({
        environment,
        kind,
        severity,
        title: title.trim(),
        description: description.trim(),
        ...bundle,
      });
      setSubmitState({
        type: response.status === "jira_failed" ? "error" : "success",
        message: response.jira_key ? `${response.message} ${response.jira_key}` : response.message,
        url: response.jira_url,
      });
      if (response.status !== "jira_failed") {
        setTitle("");
        setDescription("");
        setKind("issue");
        setSeverity("medium");
      }
    } catch (error) {
      setSubmitState({
        type: "error",
        message: error instanceof Error ? error.message : "Could not save feedback.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const trigger = isDev ? null : (
    <button
      className="thread-feedback-button"
      type="button"
      onClick={() => setOpen(true)}
      data-feedback-exclude="true"
    >
      Report
    </button>
  );

  return (
    <>
      {trigger}

      <div data-feedback-exclude="true">
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Content maxWidth="540px">
            <Dialog.Title>Report from Thread</Dialog.Title>
            <Dialog.Description size="2" color="gray">
              Send the current page, recent activity, and optional screenshot to Thread intake.
            </Dialog.Description>

            <form className="thread-feedback-dialog__form" onSubmit={handleSubmit}>
              <div className="thread-feedback-dialog__row">
                <label className="thread-feedback-dialog__field">
                  <span className="thread-feedback-dialog__label">Type</span>
                  <select
                    className="thread-feedback-dialog__select"
                    value={kind}
                    onChange={(event) => setKind(event.target.value as FeedbackKind)}
                  >
                    <option value="issue">Issue</option>
                    <option value="error">Error</option>
                    <option value="observation">Observation</option>
                  </select>
                </label>

                <label className="thread-feedback-dialog__field">
                  <span className="thread-feedback-dialog__label">Severity</span>
                  <select
                    className="thread-feedback-dialog__select"
                    value={severity}
                    onChange={(event) => setSeverity(event.target.value as FeedbackSeverity)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="blocker">Blocker</option>
                  </select>
                </label>
              </div>

              <label className="thread-feedback-dialog__field">
                <span className="thread-feedback-dialog__label">Summary</span>
                <input
                  className="thread-feedback-dialog__input"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength={160}
                  placeholder="What should the report say?"
                  required
                />
              </label>

              <label className="thread-feedback-dialog__field">
                <span className="thread-feedback-dialog__label">Details</span>
                <textarea
                  className="thread-feedback-dialog__textarea"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  maxLength={4000}
                  placeholder="What happened, what did you expect, and what were you doing?"
                  required
                />
              </label>

              <label className="thread-feedback-dialog__checkbox">
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={(event) => setIncludeScreenshot(event.target.checked)}
                />
                Attach screenshot of this page
              </label>

              {submitState.type !== "idle" && (
                <div
                  className={`thread-feedback-dialog__status thread-feedback-dialog__status--${submitState.type}`}
                  role={submitState.type === "error" ? "alert" : "status"}
                >
                  {submitState.type === "success" && submitState.url ? (
                    <a href={submitState.url} target="_blank" rel="noreferrer">
                      {submitState.message}
                    </a>
                  ) : (
                    submitState.message
                  )}
                </div>
              )}

              <Flex gap="3" justify="end" align="center">
                <Dialog.Close>
                  <Button type="button" variant="soft" color="gray">
                    Close
                  </Button>
                </Dialog.Close>
                <Button type="submit" disabled={!canSubmit}>
                  {submitting ? "Sending..." : "Send report"}
                </Button>
              </Flex>
              <Text size="1" color="gray">
                Feedback is saved in Thread first, then routed to Jira when configured.
              </Text>
            </form>
          </Dialog.Content>
        </Dialog.Root>
      </div>
    </>
  );
}
